import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, useState } from 'react';
import { type GuildMember, type Task } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import { STATUS_CONFIG } from '../lib/taskStatus';
import { compareTaskRecency } from '../lib/taskOrdering';
import { UserAvatar } from './UserAvatar';
import styles from './MemberProjectProfilePopover.module.css';

type LoadState = { kind: 'loading' } | { kind: 'ready' } | { kind: 'error' };

function relationFor(task: Task, userId: string): MessageDescriptor {
  if (task.assignee_id === userId) return msg`Assigned`;
  if (task.created_by === userId) return msg`Created`;
  return msg`Participant`;
}

function TaskRow({ task, userId }: { task: Task; userId: string }) {
  const { t, i18n } = useLingui();
  const status = STATUS_CONFIG[task.status];
  return (
    <li className={styles.task}>
      <span className={styles.status} style={{ background: status.color }} aria-hidden="true" />
      <span className={styles.taskCopy}>
        <strong title={task.title}>{task.title}</strong>
        <small>
          {i18n._(status.label)} · {i18n._(relationFor(task, userId))}
        </small>
      </span>
    </li>
  );
}

/** A compact Project-member profile based solely on the authorized task read model. */
export default function MemberProjectProfilePopover({
  guildId,
  member,
  isProjectOwner = false,
}: {
  guildId: string;
  member: GuildMember;
  isProjectOwner?: boolean;
}) {
  const { t, i18n } = useLingui();
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const taskById = useTaskStore((state) => state.byTaskId);

  useEffect(() => {
    let stale = false;
    setLoadState({ kind: 'loading' });
    useTaskStore
      .getState()
      .loadGuildTasks(guildId)
      .then(() => {
        if (!stale) setLoadState({ kind: 'ready' });
      })
      .catch(() => {
        if (!stale) setLoadState({ kind: 'error' });
      });
    return () => {
      stale = true;
    };
  }, [guildId]);

  const work = useMemo(() => {
    if (loadState.kind !== 'ready') return null;
    const related = (Object.values(taskById) as Task[])
      .filter((task) => task.guild_id === guildId)
      .filter(
        (task) =>
          task.created_by === member.user_id ||
          task.assignee_id === member.user_id ||
          task.participants?.includes(member.user_id),
      )
      .sort(compareTaskRecency);
    const current = related.filter(
      (task) =>
        task.status === 'in_progress' || task.status === 'in_review' || task.status === 'open',
    );
    const completed = related.filter((task) => task.status === 'done');
    return {
      active: related.filter((task) => task.status === 'in_progress' || task.status === 'in_review')
        .length,
      open: related.filter((task) => task.status === 'open').length,
      completed: completed.length,
      current: current.slice(0, 2),
      recent: completed.slice(0, 1),
    };
  }, [guildId, loadState, member.user_id, taskById]);

  const joined = member.joined_at
    ? i18n.date(new Date(member.joined_at), { dateStyle: 'medium' })
    : null;
  return (
    <section className={styles.root} aria-label={t`${member.display_name} Project profile`}>
      <header className={styles.identity}>
        <UserAvatar
          userId={member.user_id}
          displayName={member.display_name}
          role={member.role}
          avatarUrl={member.avatar_url}
          state={member.state}
          size={32}
        />
        <div className={styles.identityMeta}>
          <strong>{member.display_name}</strong>
          {member.role === 'agent' && (
            <span className={styles.agentLabel}>
              <Trans>APP</Trans>
            </span>
          )}
          {member.role !== 'agent' && isProjectOwner && (
            <span className={styles.agentLabel}>
              <Trans>OWNER</Trans>
            </span>
          )}
          {member.role !== 'agent' && !isProjectOwner && member.membership_role === 'admin' && (
            <span className={styles.agentLabel}>
              <Trans>ADMIN</Trans>
            </span>
          )}
          {joined && (
            <span>
              <Trans>Joined {joined}</Trans>
            </span>
          )}
        </div>
      </header>
      <div className={styles.content}>
        <section aria-labelledby="contribution-summary">
          <h2 id="contribution-summary">
            <Trans>In this Project</Trans>
          </h2>
          {loadState.kind === 'loading' && (
            <p className={styles.muted} role="status">
              <Trans>Loading authorized task context…</Trans>
            </p>
          )}
          {loadState.kind === 'error' && (
            <p className={styles.muted} role="alert">
              <Trans>Task context is unavailable right now.</Trans>
            </p>
          )}
          {work && (
            <div className={styles.summary} aria-label={t`Task contribution summary`}>
              <span>
                <Trans>
                  <strong>{work.active}</strong> active
                </Trans>
              </span>
              <span>
                <Trans>
                  <strong>{work.open}</strong> open
                </Trans>
              </span>
              <span>
                <Trans>
                  <strong>{work.completed}</strong> completed
                </Trans>
              </span>
            </div>
          )}
        </section>
        {work && (
          <section aria-labelledby="current-focus">
            <h2 id="current-focus">
              <Trans>Current focus</Trans>
            </h2>
            {work.current.length ? (
              <ul className={styles.list}>
                {work.current.map((task) => (
                  <TaskRow key={task.id} task={task} userId={member.user_id} />
                ))}
              </ul>
            ) : (
              <p className={styles.muted}>
                <Trans>No open work in the authorized task view.</Trans>
              </p>
            )}
          </section>
        )}
        {work && work.recent.length > 0 && (
          <section aria-labelledby="recent-work">
            <h2 id="recent-work">
              <Trans>Recent work</Trans>
            </h2>
            <ul className={styles.list}>
              {work.recent.map((task) => (
                <TaskRow key={task.id} task={task} userId={member.user_id} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </section>
  );
}
